import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

/**
 * AnimatedIconButton
 * A minimal, premium-feel icon-only button with subtle micro‑interactions.
 * - Smooth hover lift + background/soft shadow
 * - Press feedback via scale
 * - Optional "destructive" styling variant
 * - Accessible (focus ring, aria-label required externally)
 */
export interface AnimatedIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'ghost' | 'danger'
  size?: 'xs' | 'sm' | 'md'
  activeIcon?: React.ReactNode
  icon: React.ReactNode
  isActive?: boolean
}

const sizeClasses: Record<string, string> = {
  xs: 'h-7 w-7 text-[13px]',
  sm: 'h-8 w-8 text-sm',
  md: 'h-9 w-9 text-base',
}

const variantClasses: Record<string, string> = {
  default: 'bg-background border border-black/10 text-foreground hover:bg-black/5',
  ghost: 'text-foreground/70 hover:text-foreground hover:bg-black/5',
  danger: 'text-red-600 hover:text-red-700 hover:bg-red-50',
}

export const AnimatedIconButton = React.forwardRef<HTMLButtonElement, AnimatedIconButtonProps>(({ 
  className,
  variant = 'ghost',
  size = 'sm',
  icon,
  activeIcon,
  isActive,
  disabled,
  // Extract common button props explicitly to avoid passing drag handlers incompatible with framer-motion typing
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  type,
  ...rest
}, ref) => {
  return (
    <motion.button
      ref={ref}
      type={type || 'button'}
      whileHover={disabled ? undefined : { y: -2 }}
      whileTap={disabled ? undefined : { scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30, mass: 0.5 }}
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center rounded-md font-medium outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-40 disabled:pointer-events-none select-none',
        'before:absolute before:inset-0 before:rounded-md before:opacity-0 before:transition-opacity before:pointer-events-none before:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08),0_2px_4px_-2px_rgba(0,0,0,0.08)] hover:before:opacity-100',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
  aria-label={(rest as any)['aria-label']}
      disabled={disabled}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <span className="relative flex items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={isActive ? 'active' : 'inactive'}
            initial={{ opacity: 0, rotate: -15, scale: 0.85 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 15, scale: 0.85 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex items-center justify-center"
          >
            {isActive && activeIcon ? activeIcon : icon}
          </motion.span>
        </AnimatePresence>
      </span>
    </motion.button>
  )
})

AnimatedIconButton.displayName = 'AnimatedIconButton'

export default AnimatedIconButton
